import { useMemo } from "react";
export const getIsServiceValid = (url, isPreviewValid) => (url && isPreviewValid) || !url;
/**
 * useIsAddNewsEnabled
 *
 * @description
 * A custom hook that determines if the "Add News" action is enabled based on various conditions.
 * It checks for the validity of the OMDB ID, loading state, news details, and service configurations.
 * The hook uses `useMemo` to optimize performance by memoizing the computed values.
 * @param {NewsEnabledProps} props - The properties required to determine if adding news is enabled.
 * @param {boolean} props.isOmdbIdValid - Indicates if the OMDB ID is valid.
 * @param {boolean} props.loading - Indicates if the system is currently loading.
 * @param {NewsAddInput} props.news - The news details being added.
 * @param {object} props.services - The services related to the news.
 * @returns
 * {boolean} - Returns true if adding news is enabled, otherwise false.
 *
 */
export const useIsAddNewsEnabled = ({ isImdbIdValid, loading, news, services }) => {
    const hasName = useMemo(() => Boolean(isImdbIdValid || news.name), [isImdbIdValid, news.name]);
    const isAddHomeVideoValid = useMemo(() => news.isAddHomeVideo && hasName, [hasName, news.isAddHomeVideo]);
    const isHomeVideoIdValid = useMemo(() => !news.isAddHomeVideo && Boolean(news.homeVideoId), [news.homeVideoId, news.isAddHomeVideo]);
    const isHomeVideoValid = useMemo(() => isAddHomeVideoValid || isHomeVideoIdValid, [isAddHomeVideoValid, isHomeVideoIdValid]);
    const areImagesValid = useMemo(() => {
        if (!news.isAddHomeVideo) {
            return true;
        }
        if (news.images?.length) {
            return true;
        }
        return false;
    }, [news.images?.length, news.isAddHomeVideo]);
    const hasValidServices = useMemo(() => getIsServiceValid(services.avNirvana.url, services.avNirvana.isPreviewValid) &&
        getIsServiceValid(services.bluRay.url, services.bluRay.isPreviewValid) &&
        getIsServiceValid(services.hidefDigest.url, services.hidefDigest.isPreviewValid) &&
        getIsServiceValid(services.whySoBlu.url, services.whySoBlu.isPreviewValid), [
        services.avNirvana.isPreviewValid,
        services.avNirvana.url,
        services.bluRay.isPreviewValid,
        services.bluRay.url,
        services.hidefDigest.isPreviewValid,
        services.hidefDigest.url,
        services.whySoBlu.isPreviewValid,
        services.whySoBlu.url
    ]);
    return useMemo(() => areImagesValid &&
        hasValidServices &&
        isHomeVideoValid &&
        news.authorId !== null &&
        !loading, [areImagesValid, hasValidServices, isHomeVideoValid, loading, news.authorId]);
};
/**
 * getIsServiceCreated
 *
 * @description
 * Determines if a news service has been created based on the selected news source and service ID.
 * @param {SelectedNewsSource} selected - The selected news source containing the URL and preview validity.
 * @param {string} serviceId - The ID of the news service.
 * @returns
 * True if the service is considered created, otherwise false.
 *
 */
export const getIsServiceCreated = (selected, serviceId) => (Boolean(selected.url) && selected.isPreviewValid && Boolean(serviceId)) ||
    !selected.url;
export const getIsNewsReadyToBeCreated = ({ avNirvana, bluRay, hidefDigest, newsSourcesIdsServiceMap, singleSource, whySoBlu }) => {
    /**
     * If Single Source is set we are ready to create
     */
    if (singleSource?.url) {
        return Boolean(singleSource?.id &&
            (singleSource?.labelId || singleSource?.outletId) &&
            singleSource?.url);
    }
    /**
     * Otherwise check that our services are ready
     */
    return (getIsServiceCreated(avNirvana, newsSourcesIdsServiceMap.avNirvana) &&
        getIsServiceCreated(bluRay, newsSourcesIdsServiceMap.bluRay) &&
        getIsServiceCreated(hidefDigest, newsSourcesIdsServiceMap.hidefDigest) &&
        getIsServiceCreated(whySoBlu, newsSourcesIdsServiceMap.whySoBlu));
};
